import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import { camelToKebab, useHoneycombTheme } from '../../theming';
import { FixMeLater } from '../../utils';
import { ModularPriceCurrency } from './ModularPriceCurrency/ModularPriceCurrency';
import { ModularPriceFormatted } from './ModularPriceFormatted/ModularPriceFormatted';
import { ModularPriceFraction } from './ModularPriceFraction/ModularPriceFraction';
import { ModularPriceMajor } from './ModularPriceMajor/ModularPriceMajor';

type StyleClasses = 'root' | 'inner';

// Static styles
const STATIC_STYLES: { [key in StyleClasses]: SerializedStyles } = {
    root: css`
        position: relative;
    `,
    inner: css`
        display: flex;
        align-items: baseline;

        [data-currency] {
            font-size: var(--font-size-currency);
            font-weight: var(--font-weight-currency);
        }

        [data-fraction] {
            font-size: var(--font-size-fraction);
            font-weight: var(--font-weight-fraction);
        }
    `,
};

export interface ModularPriceProps {
    /**
     * The `ModularPrice` child components.
     */
    children?: React.ReactNode;
}

export const ModularPrice: React.ForwardRefExoticComponent<ModularPriceProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function ModularPrice(
        props: ModularPriceProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const { children, ...other } = props;

        const { theme } = useHoneycombTheme();
        const componentTheme = theme.components.themes.ModularPrice;

        const { currencyFontWeight, currencySizeCoefficient, fractionFontWeight, fractionSizeCoefficent } =
            componentTheme;

        // Prepare CSS variables
        const cssVariables: React.CSSProperties = React.useMemo(() => {
            return {
                '--font-size-currency': `${currencySizeCoefficient}em`,
                '--font-size-fraction': `${fractionSizeCoefficent}em`,
                '--font-weight-currency': `var(--hc-font-weight-${camelToKebab(currencyFontWeight)})`,
                '--font-weight-fraction': `var(--hc-font-weight-${camelToKebab(fractionFontWeight)})`,
            } as React.CSSProperties;
        }, [currencyFontWeight, currencySizeCoefficient, fractionFontWeight, fractionSizeCoefficent]);

        const formatted = React.Children.toArray(children).find(
            (child) => React.isValidElement(child) && child.type === ModularPriceFormatted
        );

        const validChildren = React.useMemo(() => {
            return React.Children.toArray(children).filter(
                (child) =>
                    React.isValidElement(child) &&
                    (child.type === ModularPriceCurrency ||
                        child.type === ModularPriceMajor ||
                        child.type === ModularPriceFraction)
            );
        }, [children]);

        return (
            <span css={STATIC_STYLES.root} {...other}>
                {/* We render the standard formatted value for accesibility. This is positioned off-screen but will be visible to screen readers. */}
                {formatted && formatted}
                {/* The modular price has the aria-hidden attribute and will be ignored by screen readers. */}
                <span ref={ref} style={cssVariables} css={STATIC_STYLES.inner} aria-hidden={true}>
                    {validChildren}
                </span>
            </span>
        );
    });
